import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useViewAs } from '@/hooks/useViewAs';

export interface Subscription {
  id: string;
  user_id: string;
  user_email: string;
  status: string;
  plan_type: string;
  plan_name: string | null;
  current_period_end: string | null;
  cancel_at_period_end?: boolean;
  cancelled_at?: string | null;
}

export function useSubscription(user: User | null) {
  const { role } = useViewAs();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setIsSubscribed(false);
      setIsLoading(false);
      return;
    }

    // Admin view-as override: emulate plan state for UI testing.
    if (role === 'member') {
      setSubscription(null);
      setIsSubscribed(false);
      setIsLoading(false);
      return;
    }
    if (role === 'premium' || role === 'founding') {
      setSubscription({
        id: 'view-as-' + role,
        user_id: user.id,
        user_email: user.email ?? '',
        status: 'active',
        plan_type: 'annual',
        plan_name: role === 'founding' ? 'Alpha with AI Club Founding' : 'Alpha with AI Club Premium',
        current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      });
      setIsSubscribed(true);
      setIsLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        const { data: hasAccess, error: accessError } = await supabase.rpc('has_active_subscription', {
          _user_id: user.id,
        });

        if (accessError || !hasAccess) {
          if (accessError) console.error('Error checking subscription access:', accessError);
          setSubscription(null);
          setIsSubscribed(false);
          return;
        }

        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .in('status', ['active', 'cancelled'])
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching subscription:', error);
          setIsSubscribed(true);
          setSubscription(null);
        } else if (data?.length) {
          const now = Date.now();
          // 48h renewal-in-flight grace for still-active subs
          const GRACE_MS = 48 * 60 * 60 * 1000;
          const validSub = data.find((row) => {
            const periodEnd = row.current_period_end ? new Date(row.current_period_end).getTime() : null;
            if (periodEnd === null) return false;
            if (periodEnd > now) return true;
            const inGrace =
              row.status === 'active' && !row.cancelled_at && !row.cancel_at_period_end;
            return inGrace && periodEnd > now - GRACE_MS;
          });
          setSubscription((validSub ?? null) as Subscription | null);
          setIsSubscribed(true);
        } else {
          setSubscription(null);
          setIsSubscribed(true);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [user, role]);

  return { subscription, isSubscribed, isLoading };
}
